export const USER_ROLES = ['超级管理员', '管理员', '子管理员', '普通用户'] as const

export interface ManagementIdentity {
  userId?: string
  role?: string
  status?: string
  monitorDashboard?: number
  mediaCooperationAmount?: number
}

export function canReceiveMediaCooperationAmount(role: unknown): boolean {
  return role === '管理员' || role === '子管理员' || role === '普通用户'
}

export function canGrantMediaCooperationAmount(actor: ManagementIdentity, target: ManagementIdentity): boolean {
  return actor.role === '超级管理员' && actor.status === '使用中' &&
    (actor.monitorDashboard ?? 0) >= 2 && !!actor.userId && !!target.userId &&
    actor.userId !== target.userId && canReceiveMediaCooperationAmount(target.role) && target.status === '使用中'
}

export function mediaCooperationAmountLabel(user: ManagementIdentity): string {
  if (user.role === '超级管理员') return '默认完整权限（2）'
  if (!canReceiveMediaCooperationAmount(user.role)) return '不适用'
  return ['无权限', '仅查看', '新增、查看、修改'][user.mediaCooperationAmount ?? 0] ?? '无权限'
}

export function ordinaryPermissionPayload<T extends { mediaCooperationAmount?: unknown }>(form: T): Omit<T, 'mediaCooperationAmount'> {
  const { mediaCooperationAmount: _amount, ...payload } = form
  return payload
}

interface MediaAmountGrantActions<T extends ManagementIdentity> {
  refreshIdentity: () => Promise<ManagementIdentity>
  fetchTarget: (userId: string) => Promise<T>
}

export async function loadMediaCooperationAmountTarget<T extends ManagementIdentity>(
  userId: string, actions: MediaAmountGrantActions<T>,
): Promise<T> {
  const actor = await actions.refreshIdentity()
  const target = await actions.fetchTarget(userId)
  if (target.userId !== userId || !canGrantMediaCooperationAmount(actor, target)) {
    throw new Error('合作金额授权权限已变化，请刷新后重试')
  }
  return target
}

export async function grantMediaCooperationAmount<T extends ManagementIdentity, R>(
  userId: string, level: number,
  actions: MediaAmountGrantActions<T> & { save: (userId: string, payload: { level: number }) => Promise<R> },
): Promise<R> {
  if (!Number.isInteger(level) || level < 0 || level > 2) throw new Error('请选择有效的权限等级')
  await loadMediaCooperationAmountTarget(userId, actions)
  return actions.save(userId, { level })
}

export function isAdministrator(role: unknown): boolean {
  return role === '超级管理员' || role === '管理员'
}

export function canGrantRole(actorRole: unknown, grantedRole: unknown, existingRole?: unknown): boolean {
  if (!USER_ROLES.some(role => role === grantedRole)) return false
  if (actorRole === '超级管理员') return true
  // 管理员可以保留同级目标的原角色，但不能把较低角色提升为管理员。
  if (actorRole === '管理员' && existingRole === '管理员' && grantedRole === '管理员') return true
  return (actorRole === '管理员' || actorRole === '子管理员') &&
    (grantedRole === '子管理员' || grantedRole === '普通用户')
}

/** 授权目标范围；状态操作还需要操作者是超级管理员或管理员。 */
export function canManageUser(actor: ManagementIdentity, target: ManagementIdentity): boolean {
  if (actor.status !== '使用中' || !actor.userId || !target.userId || actor.userId === target.userId) return false
  if (!USER_ROLES.some(role => role === target.role)) return false
  if (actor.role === '超级管理员') return true
  if (actor.role === '管理员') return target.role === '管理员' || target.role === '子管理员' || target.role === '普通用户'
  return actor.role === '子管理员' && (target.role === '子管理员' || target.role === '普通用户')
}

export function roleRank(role: unknown): number {
  const rank = USER_ROLES.findIndex(value => value === role)
  return rank < 0 ? USER_ROLES.length : rank
}
